import * as generated from './generated'
import type { ApiOperation } from './generated'

/**
 * Readable names for the core's generated operations.
 *
 * ## Why this file exists
 *
 * API Platform derives `operationId` from the route, so the generated surface is spelled
 * `api_v1assets_assetIdoperations_post` rather than `requestOperation`. Those names are the
 * server's, and `src/api/generated/` is overwritten wholesale by `npm run api:sync` — nothing may
 * be renamed there.
 *
 * ## Why this is not the drift that got us here
 *
 * The audit's finding was a hand-written *specification*: a file that described operations nobody
 * had implemented, and which no build step ever compared against the server. This file describes
 * nothing. Every binding below is a direct reference to a generated symbol, so if the core renames,
 * moves or removes an operation, the regenerated module no longer exports that symbol and **this
 * file fails to compile**. An alias cannot outlive the thing it aliases.
 *
 * That is also why the aliases are `const` re-exports and not a `Record<string, ApiOperation>`
 * lookup table: a string key would be resolved at runtime and would fail on a screen instead of in
 * CI.
 *
 * Add an alias only for an operation the dashboard actually calls. An alias for an operation no
 * screen uses is a claim about the product that no test will ever check.
 */

// --- session ---------------------------------------------------------------------------------

/**
 * `POST /api/v1/auth/token`. Exchange email + password for an access token.
 *
 * **This does not produce a usable session.** Design §11.4 makes MFA mandatory for this surface,
 * so the token this returns carries `mfa: false` and every other endpoint answers
 * `403 MFA_REQUIRED` until {@link verifyMfa} has been completed. See `src/stores/auth.ts`.
 */
export const authenticate = generated.login_check_post

/** `POST /api/v1/auth/mfa/challenge`. Ask what second factors this account may present. */
export const challengeMfa = generated.api_v1authmfachallenge_post

/** `POST /api/v1/auth/mfa/verify`. Present the second factor; returns the elevated access token. */
export const verifyMfa = generated.api_v1authmfaverify_post

/** `GET /api/v1/auth/me`. Identity, roles, permissions and MFA state for the current token. */
export const getSession = generated.api_v1authme_get

/** `POST /api/v1/auth/refresh`. Rotate the refresh token and mint a new access token. */
export const refreshSession = generated.api_v1authrefresh_post

/** `POST /api/v1/auth/revoke`. Sign out — this session, or every session for the account. */
export const revokeSession = generated.api_v1authrevoke_post

/** `POST /api/v1/auth/reauthenticate`. Single-use ticket for a destructive capability (§11.4). */
export const reauthenticate = generated.api_v1authreauthenticate_post

// --- assets ----------------------------------------------------------------------------------

/** `GET /api/v1/assets`. One list for every asset kind — `Asset` is the root object (§6.1). */
export const listAssets = generated.api_v1assets_get_collection

/** `GET /api/v1/assets/{id}`. */
export const getAsset = generated.api_v1assets_id_get

/**
 * `GET /api/v1/assets/{assetId}/capabilities`.
 *
 * The declared capability set for one asset. **The only input to the action panel**; see
 * `src/capabilities/registry.ts`.
 */
export const listAssetCapabilities = generated.api_v1assets_assetIdcapabilities_get_collection

/** `GET /api/v1/assets/{assetId}/manifest`. The manifest as fetched, and when it was fetched. */
export const getAssetManifest = generated.api_v1assets_assetIdmanifest_get

/** `GET /api/v1/assets/{assetId}/extensions`. Gated on `extensions.list`. */
export const listAssetExtensions = generated.api_v1assets_assetIdextensions_get_collection

/**
 * `POST /api/v1/assets`.
 *
 * Accepts `endpoint`, `domain` and `server`. **Not `site`** — a capability manifest may only come
 * from an agent, and a site with no agent is an endpoint, which is what the API's 422 says. Register
 * it as an endpoint to probe it now, and its site profile appears when its agent enrolls.
 */
export const createAsset = generated.api_v1assets_post

/** `PATCH /api/v1/assets/{id}`. Partial: label, group and tags. */
export const patchAsset = generated.api_v1assets_id_patch

/**
 * `DELETE /api/v1/assets/{id}` — **archive, not destroy.**
 *
 * The verb is DELETE and the effect is a timestamp: the asset stops being probed and drops out of
 * the default list, and every event, metric and uptime transition it ever produced is still there.
 * `restoreAsset` puts it back. Nothing in this API erases history, which is the point of an audit
 * system.
 */
export const archiveAsset = generated.api_v1assets_id_delete

/** `POST /api/v1/assets/{id}/restore`. */
export const restoreAsset = generated.api_v1assets_idrestore_post

/** `GET /api/v1/groups`. */
export const listGroups = generated.api_v1groups_get_collection

/** `POST /api/v1/groups`. */
export const createGroup = generated.api_v1groups_post

// --- enrollment ---------------------------------------------------------------------------------

/**
 * `POST /api/v1/enrollment-tokens` — mint a one-time token for an agent to redeem.
 *
 * **The plaintext is in this response and nowhere else.** It is stored hashed, so it cannot be
 * shown again; a caller that discards the response has to mint another. That is why the UI reveals
 * it once, prominently, with a copy button, rather than tucking it into a list it can be navigated
 * away from.
 */
export const issueEnrollmentToken = generated.api_v1enrollment_tokens_post

/** `GET /api/v1/enrollment-tokens`. Metadata only — never the plaintext. */
export const listEnrollmentTokens = generated.api_v1enrollment_tokens_get_collection

/** `DELETE /api/v1/enrollment-tokens/{id}`. Revokes an unredeemed token. */
export const revokeEnrollmentToken = generated.api_v1enrollment_tokens_id_delete

// --- operations ------------------------------------------------------------------------------

/**
 * `POST /api/v1/assets/{assetId}/operations` — **the product's core verb.**
 *
 * Request an operation on an asset. Gated on the asset's declared capabilities; destructive
 * capabilities additionally require a re-authentication ticket, supplied either as
 * `reauthentication_ticket` in the body or as the `X-Reauthentication` header.
 *
 * Note the shape: the asset is in the path, not in the body. The dashboard's previous, invented
 * spec had a flat `POST /operations` with `asset_id` in the body; it never existed.
 */
export const requestOperation = generated.api_v1assets_assetIdoperations_post

/** `GET /api/v1/operations`. */
export const listOperations = generated.api_v1operations_get_collection

/** `GET /api/v1/assets/{assetId}/operations`. The same list, scoped to one asset. */
export const listAssetOperations = generated.api_v1assets_assetIdoperations_get_collection

/** `GET /api/v1/operations/{id}`. Polled by the watch loop until the state is terminal. */
export const getOperation = generated.api_v1operations_id_get

/**
 * `POST /api/v1/operations/{id}/retry`.
 *
 * Allocates a **new** operation. `op_id` is an idempotency key, so re-submitting one returns the
 * stored terminal state rather than doing the work again (`capabilities.md` §3.3).
 */
export const retryOperation = generated.api_v1operations_idretry_post

// --- activity --------------------------------------------------------------------------------

/** `GET /api/v1/events`. */
export const searchEvents = generated.api_v1events_get_collection

/** `GET /api/v1/event-statistics`. Counts per bucket — what the activity chart reads. */
export const getEventStatistics = generated.api_v1event_statistics_get_collection

// --- metrics ---------------------------------------------------------------------------------

/** `GET /api/v1/metrics`. One series per (asset, metric, tag combination). */
export const getMetricSeries = generated.api_v1metrics_get_collection

/** `GET /api/v1/assets/{assetId}/uptime`. Uptime roll-up for one asset over a window. */
export const getAssetUptime = generated.api_v1assets_assetIduptime_get

// --- alerting --------------------------------------------------------------------------------

/** `GET /api/v1/alert-rules`. */
export const listAlertRules = generated.api_v1alert_rules_get_collection

/** `POST /api/v1/alert-rules`. */
export const createAlertRule = generated.api_v1alert_rules_post

/**
 * `PATCH /api/v1/alert-rules/{id}`.
 *
 * A merge patch, not a `PUT`. The server offers no `PUT` on this resource, so "send the whole rule
 * back" — which the previous client did — was describing an endpoint that does not exist.
 */
export const patchAlertRule = generated.api_v1alert_rules_id_patch

/** `DELETE /api/v1/alert-rules/{id}`. */
export const deleteAlertRule = generated.api_v1alert_rules_id_delete

// --- the guard rail --------------------------------------------------------------------------

/**
 * Every alias above, as data.
 *
 * Exists so a test can assert that each alias still points at a live descriptor with a path under
 * `/api/v1` — that is, that the aliases and the specification have not come apart. The type
 * annotation is the interesting part: it is what makes a stale alias a compile error.
 */
export const NAMED_OPERATIONS: Readonly<
  Record<string, ApiOperation<never, never, never, unknown, never>>
> = {
  authenticate,
  challengeMfa,
  verifyMfa,
  getSession,
  refreshSession,
  revokeSession,
  reauthenticate,
  listAssets,
  getAsset,
  listAssetCapabilities,
  getAssetManifest,
  listAssetExtensions,
  listGroups,
  requestOperation,
  listOperations,
  listAssetOperations,
  getOperation,
  retryOperation,
  searchEvents,
  getEventStatistics,
  getMetricSeries,
  getAssetUptime,
  listAlertRules,
  createAlertRule,
  patchAlertRule,
  deleteAlertRule,
} as unknown as Readonly<Record<string, ApiOperation<never, never, never, unknown, never>>>
